import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, LogOut, Menu, Sun, Moon, Home, Calendar, Users, Radio, FileText, ClipboardList, MessagesSquare, LifeBuoy, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { enterStudio, removeRepresentativeToken, getCommUnreadCount, getMyProfile } from '../services/representativeService';
import { getGroups as getSupportGroups } from '../services/representativeSupportService';
import { hasSupportQueue } from './support/v3/queueModel';
import { circolariRappresentanteApi } from '../services/circolariRappresentanteService';
import { saveToken, saveAssociateData, logout as associateLogout } from '../services/otpAuthService';
import { tipoRappresentante } from '../utils/tipoRappresentante';
import '../styles/associative-area/app.css';

type AssocRoute = 'dashboard' | 'calendario' | 'associati' | 'diretta' | 'comunicazioni' | 'supporto' | 'circolari' | 'sondaggi';

interface AssociativeAppShellProps {
  children: React.ReactNode;
  nome?: string;
  cognome?: string;
  caricaPrincipale?: string;
  tipo?: 'interno' | 'esterno';
  route?: AssocRoute;
  associatesAccess?: boolean;
  canLive?: boolean;
  /** Carica con il permesso associativa.circolari.read: mostra la voce «Circolari» */
  canCircolari?: boolean;
  /** Carica con il permesso associativa.sondaggi.read: mostra la voce «Sondaggi» (se assente, la shell legge il profilo) */
  canSondaggi?: boolean;
  /** Carica con il permesso associativa.eventi.read: mostra la voce «Calendario Attività» (se assente, la shell legge il profilo) */
  canCalendario?: boolean;
  foto?: string;
  perimetro?: string;
  caricaDal?: string;
}

const CRUMB_LABEL: Record<AssocRoute, string> = {
  dashboard: 'Dashboard',
  calendario: 'Calendario Attività',
  associati: 'Associati',
  diretta: 'Diretta Live',
  comunicazioni: 'Comunicazioni',
  supporto: 'Supporto',
  circolari: 'Circolari',
  sondaggi: 'Sondaggi',
};

const UPLOADS_BASE = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:5000';

const AssociativeAppShell: React.FC<AssociativeAppShellProps> = ({
  children,
  nome,
  cognome,
  caricaPrincipale,
  tipo,
  route = 'dashboard',
  associatesAccess,
  canLive,
  canCircolari,
  canSondaggi,
  canCalendario,
  foto,
  perimetro,
  caricaDal,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { theme, toggleTheme } = useTheme();
  const [entering, setEntering] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Sidebar collassata (solo desktop), preferenza ricordata tra le sessioni
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('assoc_sidebar_collapsed') === '1');
  const [commUnread, setCommUnread] = useState(0);
  // Comunicazioni abilitate per la carica? L'endpoint unread-count e gated dal
  // permesso associativa.comunicazioni.read: un 403 = carica disabilitata → nascondi voce.
  const [commEnabled, setCommEnabled] = useState(true);
  // Supporto abilitato per la carica? `/support/groups` e gated da
  // associativa.supporto.read: un 403 = carica senza il permesso → voce nascosta.
  // Anche con il permesso, senza canali di cui si e componente (`mine` vuoto) la
  // coda sarebbe sempre vuota: voce nascosta lo stesso, A MENO CHE ci sia almeno
  // un ticket attivo assegnato come esterno al canale (`assignedCount`, Task 26).
  const [supportEnabled, setSupportEnabled] = useState(true);
  // Circolari: se la pagina non passa `canCircolari`, la shell sonda l'archivio
  // (`/representative/circolari/anni`, gated da associativa.circolari.read): così la
  // voce resta visibile anche nelle pagine che non calcolano il permesso.
  const [circolariSondate, setCircolariSondate] = useState(false);

  useEffect(() => {
    if (canCircolari !== undefined) return;
    let cancelled = false;
    circolariRappresentanteApi.getAnniCircolari()
      .then(() => { if (!cancelled) setCircolariSondate(true); })
      .catch(() => { if (!cancelled) setCircolariSondate(false); });
    return () => { cancelled = true; };
  }, [route, canCircolari]);
  const mostraCircolari = canCircolari !== undefined ? canCircolari : circolariSondate;

  // Sondaggi e Calendario: se la pagina non passa `canSondaggi` o `canCalendario`,
  // la shell legge una volta il profilo (`/representative/me`) e cerca i permessi
  // associativa.sondaggi.read e associativa.eventi.read. Su errore le voci restano
  // nascoste: niente toast né redirect (li gestisce la pagina).
  const [permessiSondati, setPermessiSondati] = useState<string[]>([]);

  useEffect(() => {
    if (canSondaggi !== undefined && canCalendario !== undefined) return;
    let cancelled = false;
    getMyProfile()
      .then((res: any) => {
        const permessi: { name?: string }[] = Array.isArray(res?.data?.permissions) ? res.data.permissions : [];
        if (!cancelled) setPermessiSondati(permessi.map(p => p?.name || ''));
      })
      .catch(() => { if (!cancelled) setPermessiSondati([]); });
    return () => { cancelled = true; };
  }, [route, canSondaggi, canCalendario]);
  const mostraSondaggi = canSondaggi !== undefined ? canSondaggi : permessiSondati.includes('associativa.sondaggi.read');
  // Senza associativa.eventi.read le rotte del calendario rispondono 403: voce nascosta.
  const mostraCalendario = canCalendario !== undefined ? canCalendario : permessiSondati.includes('associativa.eventi.read');

  // Voce «Supporto»: componente di un canale, assegnatario esterno o invitato
  // in un ticket (`invitedCount`, parte B).
  useEffect(() => {
    let cancelled = false;
    getSupportGroups()
      .then((res) => { if (!cancelled) setSupportEnabled(hasSupportQueue(res)); })
      .catch((err: any) => { if (!cancelled && err?.response?.status === 403) setSupportEnabled(false); });
    return () => { cancelled = true; };
  }, [route]);

  // Badge messaggi non letti sulla voce Comunicazioni (refresh al mount e al focus finestra)
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      getCommUnreadCount()
        .then(res => { if (!cancelled) { setCommUnread(res.unread || 0); setCommEnabled(true); } })
        .catch((err: any) => { if (!cancelled && err?.response?.status === 403) setCommEnabled(false); });
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refresh);
    };
  }, [route]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem('assoc_sidebar_collapsed', c ? '0' : '1');
      return !c;
    });
  };

  const handleLogout = () => {
    removeRepresentativeToken();
    associateLogout();
    navigate('/accesso-associati');
  };

  // Esterno (solo area associativa, nessuno studio): il pulsante in alto porta alla
  // dashboard dell'area. Interno o tipo non noto: tenta l'ingresso nello studio.
  // Pagina senza `tipo` (es. elenco associati): lo si legge dal token in memoria.
  const esterno = tipoRappresentante(tipo) === 'esterno';

  const handleAreaRiservata = async () => {
    if (esterno) {
      navigate('/area-associativa');
      return;
    }
    setEntering(true);
    try {
      const result = await enterStudio();
      const { token, associate } = result.data;
      saveToken(token);
      saveAssociateData(associate);
      navigate('/area-riservata');
    } catch (error: any) {
      if (error?.response?.status === 400) {
        navigate('/area-associativa');
        return;
      }
      const msg = error?.response?.data?.error || error?.message || 'Impossibile accedere all\'area riservata dello studio';
      toast.error(msg);
    } finally {
      setEntering(false);
    }
  };

  const goTo = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const initials = `${nome?.[0] || ''}${cognome?.[0] || ''}`.toUpperCase() || 'AC';

  const NavItem: React.FC<{
    active: boolean;
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    soon?: boolean;
    badge?: number;
  }> = ({ active, icon, label, onClick, disabled, soon, badge }) => (
    <button
      type="button"
      className={'nav-item' + (disabled ? ' disabled' : '')}
      aria-current={active ? 'true' : 'false'}
      onClick={disabled ? undefined : onClick}
      title={collapsed ? label : undefined}
    >
      {icon} <span className="nav-text">{label}</span>
      {soon && <span className="soon">In arrivo</span>}
      {!!badge && badge > 0 && <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>}
    </button>
  );

  return (
    <div className="assoc-app" data-drawer={drawerOpen ? 'open' : 'closed'} data-side={collapsed ? 'collapsed' : 'open'}>
      {/* Sidebar */}
      <aside className="side">
        <div className="brand">
          <img src="/images/logo-unasca.png" alt="UNASCA" />
          <span className="area-tag">Rappresentanza</span>
          <button
            className="side-collapse"
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}
            title={collapsed ? 'Espandi menu' : 'Comprimi menu'}
          >
            {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
          </button>
        </div>

        {caricaPrincipale && (
          <div className="role-card">
            <div className="k">Carica attiva</div>
            <div className="v">{caricaPrincipale}</div>
            <div className="d">
              {perimetro}
              {perimetro && caricaDal ? ' · ' : ''}
              {caricaDal ? `dal ${new Date(caricaDal).toLocaleDateString('it-IT')}` : ''}
            </div>
          </div>
        )}

        <nav aria-label="Navigazione area">
          <div className="nav-label">Gestione</div>
          <NavItem active={route === 'dashboard'} icon={<Home size={17} />} label="Dashboard" onClick={() => goTo('/area-associativa')} />
          {mostraCalendario && (
            <NavItem active={route === 'calendario'} icon={<Calendar size={17} />} label="Calendario Attività" onClick={() => goTo('/area-associativa/calendario')} />
          )}
          {associatesAccess && (
            <NavItem active={route === 'associati'} icon={<Users size={17} />} label="Associati" onClick={() => goTo('/area-associativa/associati')} />
          )}

          <div className="nav-label">Strumenti</div>
          {canLive && (
            <NavItem active={route === 'diretta'} icon={<Radio size={17} />} label="Diretta Live" onClick={() => goTo('/area-associativa/diretta')} />
          )}
          {commEnabled && (
            <NavItem
              active={route === 'comunicazioni'}
              icon={<MessagesSquare size={17} />}
              label="Comunicazioni"
              onClick={() => goTo('/area-associativa/comunicazioni')}
              badge={commUnread}
            />
          )}
          {supportEnabled && (
            <NavItem
              active={route === 'supporto'}
              icon={<LifeBuoy size={17} />}
              label="Supporto"
              onClick={() => goTo('/area-associativa/supporto')}
            />
          )}
          {mostraCircolari && (
            <NavItem
              active={route === 'circolari'}
              icon={<FileText size={17} />}
              label="Circolari"
              onClick={() => goTo('/area-associativa/circolari')}
            />
          )}
          {mostraSondaggi && (
            <NavItem active={route === 'sondaggi'} icon={<ClipboardList size={17} />} label="Sondaggi" onClick={() => goTo('/area-associativa/sondaggi')} />
          )}
          <NavItem active={false} icon={<FileText size={17} />} label="Documenti" disabled soon />
        </nav>

        <div className="side-foot">Unasca Studi · dal 1967</div>
      </aside>

      {/* Topbar */}
      <header className="topbar">
        <button className="top-link hamburger" type="button" onClick={() => setDrawerOpen((o) => !o)} aria-label="Menu">
          <Menu size={18} />
        </button>
        <div className="crumb">
          <Shield size={14} />
          <b>Area Rappresentativa</b>
          <span>/</span>
          <span className="leaf">{CRUMB_LABEL[route]}</span>
        </div>
        <div className="spacer" />
        <button className="top-link" type="button" onClick={toggleTheme} aria-label="Cambia tema" title="Cambia tema">
          {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </button>
        <button
          className="top-link"
          type="button"
          onClick={handleAreaRiservata}
          disabled={entering}
          title={esterno ? 'Dashboard dell’area associativa' : undefined}
        >
          {esterno ? <Home size={14} aria-hidden="true" /> : <ArrowLeft size={14} />}{' '}
          {entering ? 'Accesso…' : esterno ? 'Area associativa' : 'Area riservata'}
        </button>
        <button className="top-link exit" type="button" onClick={handleLogout}>
          <LogOut size={14} /> Esci
        </button>
        <div className="top-avatar">
          <div className="ava">
            {foto ? <img src={`${UPLOADS_BASE}${foto}`} alt="" /> : initials}
          </div>
          {nome && (
            <div className="who">
              <div className="n">{nome} {cognome}</div>
              {caricaPrincipale && <div className="r">{caricaPrincipale}</div>}
            </div>
          )}
        </div>
      </header>

      {/* Backdrop drawer mobile */}
      <div className="side-backdrop" onClick={() => setDrawerOpen(false)} />

      {/* Contenuto */}
      <main className="main">{children}</main>
    </div>
  );
};

export default AssociativeAppShell;
